import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, ChevronRight, Plus, Settings, Eye, EyeOff, Trash2, AlertTriangle, Edit2 } from "lucide-react";
import { api, adminAPI } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import Modal from "../components/Modal";
import "./EventSelector.css";

const EventSelector = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  // Hard Delete Modal State
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      // Use the newly updated endpoint that handles admin visibility
      const res = await api.get("/admin/events");
      setEvents(res.data);
    } catch (error) {
      console.error("Failed to load events", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectEvent = (event) => {
    // Only allow selecting if it's active or if the user is an admin
    if (!event.is_active && !user?.is_admin) return;
    
    localStorage.setItem("current_event_id", event.id);
    navigate(`/event/${event.id}`);
  };

  const handleToggleVisibility = async (e, event) => {
    e.stopPropagation(); // Don't trigger select
    setTogglingId(event.id);
    try {
      await adminAPI.toggleEventActive(event.id);
      await fetchEvents(); // Wait for fetch
    } catch (err) {
      console.error("Failed to toggle visibility", err);
      alert("Failed to update event visibility.");
    } finally {
      setTogglingId(null);
    }
  };

  const handleHardDeleteClick = (e, id) => {
    e.stopPropagation(); // Don't trigger select
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmHardDelete = async () => {
    setDeleting(true);
    try {
      await adminAPI.hardDeleteEvent(deleteId);
      setShowDeleteModal(false);
      fetchEvents(); // Refresh list
    } catch (err) {
      console.error("Failed to delete event", err);
      alert("Failed to permanently delete event. Make sure it has no active dependencies if CASCADE fails.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="event-selector-page">
      <div className="selector-hero">
        <h1>Select Event</h1>
        <p>Choose an event to explore the schedule and sessions</p>

        {user?.is_admin && (
          <div className="admin-actions" style={{ marginTop: "24px" }}>
            <button
              className="admin-btn-outline"
              onClick={() => navigate("/admin")}
            >
              <Settings size={18} /> Admin Dashboard
            </button>
          </div>
        )}
      </div>

      <div className="events-container">
        <div className="events-list">
          {loading ? (
            <div className="empty-state">Loading amazing events...</div>
          ) : events.length > 0 ? (
            events.map((evt, index) => (
              <div
                key={evt.id}
                className={`event-card ${!evt.is_active ? 'inactive' : ''}`}
                onClick={() => handleSelectEvent(evt)}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="event-date-badge">
                  <span className="month">
                    {new Date(evt.start_date).toLocaleString("default", {
                      month: "short",
                    })}
                  </span>
                  <span className="day">
                    {new Date(evt.start_date).getDate()}
                  </span>
                </div>
                <div className="event-info">
                  <h3>{evt.title}</h3>
                  <div className="event-location">
                    <MapPin size={14} /> {evt.location}
                  </div>
                  {!evt.is_active && <span className="inactive-badge">Hidden from users</span>}
                </div>

                {user?.is_admin ? (
                  <div className="event-admin-actions">
                    <button 
                      className="event-btn" 
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/admin/edit-event/${evt.id}`);
                      }}
                      title="Edit Event"
                    >
                      <Edit2 size={18} />
                    </button>
                    <button 
                      className={`event-btn ${togglingId === evt.id ? 'loading' : ''}`}
                      onClick={(e) => handleToggleVisibility(e, evt)}
                      title={evt.is_active ? "Hide from users" : "Show to users"}
                      disabled={togglingId === evt.id}
                    >
                      {togglingId === evt.id ? (
                        <div className="icon-spinner" />
                      ) : evt.is_active ? (
                        <Eye size={18} />
                      ) : (
                        <EyeOff size={18} />
                      )}
                    </button>
                    <button 
                      className="event-btn delete" 
                      onClick={(e) => handleHardDeleteClick(e, evt.id)}
                      title="Permanently Delete"
                      disabled={togglingId === evt.id || deleting}
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                ) : (
                  <ChevronRight size={20} className="event-arrow" />
                )}
              </div>
            ))
          ) : (
            <div className="empty-state">
              <p>No active events found.</p>
              {user?.is_admin && <p>Be the first to create one!</p>}
            </div>
          )}
        </div>
      </div>

      {user?.is_admin && (
        <button
          className="fab-add"
          onClick={() => navigate("/admin/create-event")}
          title="Create New Event"
        >
          <Plus size={32} />
        </button>
      )}

      {/* Hard Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => !deleting && setShowDeleteModal(false)}
        title="Permanently Delete Event?"
        size="sm"
      >
        <div style={{ padding: "20px 0", textAlign: "center" }}>
          <div style={{ 
            width: "60px", 
            height: "60px", 
            background: "#fee2e2", 
            color: "#ef4444", 
            borderRadius: "50%", 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "center", 
            margin: "0 auto 16px" 
          }}>
            <Trash2 size={30} />
          </div>
          <h3 style={{ marginBottom: "8px", color: "#1e293b" }}>Are you sure?</h3>
          <p style={{ color: "#64748b", fontSize: "0.9rem", lineHeight: "1.5" }}>
            This will permanently remove the event and <b>ALL associated sessions, masterclasses, and registrations</b>. This action cannot be undone.
          </p>
        </div>
        <div className="modal-actions" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
          <button
            className="modal-btn modal-btn-secondary"
            onClick={() => setShowDeleteModal(false)}
            disabled={deleting}
          >
            Cancel
          </button>
          <button
            className="modal-btn modal-btn-danger"
            onClick={confirmHardDelete}
            disabled={deleting}
          >
            {deleting ? "Deleting..." : "Permanently Delete"}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default EventSelector;
